'use client';

import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface DeliveryRatesProps {
  timeRange: string;
  detailed?: boolean;
}

const mockDeliveryData = {
  successRate: 94.5,
  onTimeRate: 92.3,
  failedDeliveries: 68,
  reattempts: 45,
  weeklyRates: [
    { week: 'Week 1', success: 95.2, onTime: 93.1, failed: 4.8 },
    { week: 'Week 2', success: 94.8, onTime: 92.5, failed: 5.2 },
    { week: 'Week 3', success: 93.9, onTime: 91.8, failed: 6.1 },
    { week: 'Week 4', success: 94.5, onTime: 92.3, failed: 5.5 },
  ],
  failureReasons: [
    { reason: 'Address Not Found', count: 18, percentage: 26.5 },
    { reason: 'Customer Unavailable', count: 15, percentage: 22.1 },
    { reason: 'Weather Delay', count: 12, percentage: 17.6 },
    { reason: 'Customs Issues', count: 10, percentage: 14.7 },
    { reason: 'Vehicle Breakdown', count: 8, percentage: 11.8 },
    { reason: 'Other', count: 5, percentage: 7.3 },
  ],
  performanceByRegion: [
    { region: 'North America', success: 96.2, onTime: 94.5, total: 503 },
    { region: 'Europe', success: 94.8, onTime: 92.1, total: 412 },
    { region: 'Asia', success: 92.5, onTime: 89.8, total: 198 },
    { region: 'Australia', success: 91.3, onTime: 88.2, total: 134 },
  ],
};

export default function DeliveryRates({ timeRange, detailed = false }: DeliveryRatesProps) {
  return (
    <div className="space-y-6">
      {/* Key Delivery Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Excellent</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Success Rate</h3>
          <p className="text-3xl font-bold text-foreground">{mockDeliveryData.successRate}%</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ClockIcon" size={24} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">+1.2%</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">On-Time Rate</h3>
          <p className="text-3xl font-bold text-foreground">{mockDeliveryData.onTimeRate}%</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="XCircleIcon" size={24} className="text-red-600" />
            <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded">-8</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Failed Deliveries</h3>
          <p className="text-3xl font-bold text-foreground">{mockDeliveryData.failedDeliveries}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ArrowPathIcon" size={24} className="text-orange-500" />
            <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">Retry</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Reattempts</h3>
          <p className="text-3xl font-bold text-foreground">{mockDeliveryData.reattempts}</p>
        </div>
      </div>

      {/* Weekly Delivery Rates Chart */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Weekly Delivery Performance</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mockDeliveryData.weeklyRates}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              formatter={(value: number) => `${value}%`}
            />
            <Legend />
            <Bar dataKey="success" fill="#10b981" name="Success Rate" />
            <Bar dataKey="onTime" fill="#3b82f6" name="On-Time Rate" />
            <Bar dataKey="failed" fill="#ef4444" name="Failed Rate" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Failure Reasons & Regional Performance */}
      {detailed && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Failure Reasons */}
          <div className="bg-card rounded-xl p-6 shadow-card border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Failure Reasons</h3>
            <div className="space-y-3">
              {mockDeliveryData.failureReasons.map((reason, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{reason.reason}</span>
                    <span className="text-sm font-bold text-foreground">{reason.count}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-red-500 h-2 rounded-full transition-all"
                      style={{ width: `${reason.percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{reason.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Regional Performance */}
          <div className="bg-card rounded-xl p-6 shadow-card border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Performance by Region</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 text-sm font-medium text-muted-foreground">Region</th>
                    <th className="text-right py-2 px-2 text-sm font-medium text-muted-foreground">Success</th>
                    <th className="text-right py-2 px-2 text-sm font-medium text-muted-foreground">On-Time</th>
                    <th className="text-right py-2 px-2 text-sm font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {mockDeliveryData.performanceByRegion.map((region, index) => (
                    <tr key={index} className="border-b border-border">
                      <td className="py-3 px-2 text-sm font-medium text-foreground">{region.region}</td>
                      <td className="py-3 px-2 text-sm text-right">
                        <span className="text-green-600 font-medium">{region.success}%</span>
                      </td>
                      <td className="py-3 px-2 text-sm text-right">
                        <span className="text-blue-600 font-medium">{region.onTime}%</span>
                      </td>
                      <td className="py-3 px-2 text-sm text-right text-foreground">{region.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}